import React, { useEffect, useRef, useState } from "react";
import api from "../../chat/api";
import Shell from "./CopilotCardShell";
import { waitForUploadStatus } from "../../../services/uploadStatus";

/* Writing the whole proposal in one pass.
 *
 * The same socket and status helper as ProposalWritingCard — this is one long
 * draft instead of a sequence of short ones, so there is nothing new to invent,
 * only a different shape of wait. It runs for minutes, and watching it is the
 * only thing there is to do, which is why this shows work rather than a spinner.
 *
 * THREE THINGS CARRY THE WAIT, because a percentage alone could not.
 *
 *   PHASES   a checklist driven by the phase name the worker sends, not by
 *            guessing from the number. The first version showed only a bar, it
 *            sat at 30% through a 30-second retrieval, and there was no way to
 *            tell that from a crash — which is exactly what it turned out to be
 *            the first time it ran for real.
 *   WORDS    counted off the stream. It moves continuously and it is the honest
 *            measure of the one step that takes the longest.
 *   TAIL     the last TAIL_CHARS of what is being written. Holding the whole
 *            draft in state and re-rendering every frame makes the card janky
 *            exactly when it should feel alive; the finished document is read
 *            in the panel, not here.
 *
 * AND A STALL NOTICE. The worker beats every 5s during the silent steps, so a
 * gap longer than STALL_MS means something is actually wrong. Saying so beats
 * sitting on a frozen bar until the 20-minute timeout.
 */

const TAIL_CHARS = 1500;
const STALL_MS = 45_000;

const PHASES = [
  { id: "read", label: "Reading the RFP" },
  { id: "outline", label: "Designing the response outline" },
  { id: "retrieve", label: "Finding evidence for each section" },
  { id: "write", label: "Writing the proposal" },
  { id: "save", label: "Saving" },
];

const Tick = ({ state }) => {
  if (state === "done") {
    return (
      <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-[#1F7D38]">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  if (state === "now") {
    return (
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#A04A97]/60 motion-reduce:hidden" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#A04A97]" />
      </span>
    );
  }
  return <span className="h-2 w-2 rounded-full bg-gray-300" />;
};

const ProposalOneGoCard = ({ message, onDone, onFailed }) => {
  const { projectId, projectName } = message;

  const [phase, setPhase] = useState("read");
  const [note, setNote] = useState("Starting…");
  const [pct, setPct] = useState(5);
  const [words, setWords] = useState(0);
  const [tail, setTail] = useState("");
  const [stalled, setStalled] = useState(false);
  const [error, setError] = useState(null);

  const startedRef = useRef(false);
  const waitRef = useRef(null);
  const tailRef = useRef(null);
  const wordsRef = useRef(0);
  const lastFrameRef = useRef(Date.now());

  useEffect(() => {
    // Writing a whole proposal is the most expensive thing this product does.
    // A second render must not start a second one.
    if (startedRef.current) return undefined;
    startedRef.current = true;

    let alive = true;
    const seen = () => {
      lastFrameRef.current = Date.now();
      setStalled(false);
    };

    (async () => {
      try {
        const res = await api.post(`/api/v1/rfp/folders/${projectId}/proposal/one-go/`, {});
        if (!alive) return;

        const wait = waitForUploadStatus({
          statusKey: res.data?.status_key,
          timeoutMs: 20 * 60 * 1000,
          onProgress: (d) => {
            if (!alive) return;
            seen();
            if (d.metadata?.phase) setPhase(d.metadata.phase);
            // Never let the bar retreat — frames can arrive out of order, and a
            // bar going backwards reads as work being undone.
            if (typeof d.progress === "number") setPct((p) => Math.max(p, d.progress));
            if (d.message) setNote(d.message);
          },
          onStream: (delta) => {
            if (!alive || !delta) return;
            seen();
            setPhase("write");
            wordsRef.current += (delta.match(/\S+/g) || []).length;
            setWords(wordsRef.current);
            setTail((prev) => (prev + delta).slice(-TAIL_CHARS));
          },
          onError: (d) => {
            if (!alive) return;
            setError(d?.message || "The proposal couldn't be written. Try again in a moment.");
            onFailed?.();
          },
        });
        waitRef.current = wait;

        const ok = await wait.promise;
        if (!alive) return;
        if (ok) {
          setPct(100);
          setPhase("done");
          setTimeout(
            () => alive && onDone?.({ projectId, projectName, words: wordsRef.current }),
            420
          );
        }
      } catch {
        if (alive) {
          setError("Couldn't start writing the proposal.");
          onFailed?.();
        }
      }
    })();

    return () => {
      alive = false;
      // The socket outlives this component otherwise.
      waitRef.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // The worker beats every 5s, so silence this long is a real stall.
  useEffect(() => {
    if (error) return undefined;
    const t = setInterval(() => {
      setStalled(Date.now() - lastFrameRef.current > STALL_MS);
    }, 5000);
    return () => clearInterval(t);
  }, [error]);

  // Stick to the bottom as text arrives, the way a terminal does.
  useEffect(() => {
    const el = tailRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [tail]);

  const activeIdx = Math.max(0, PHASES.findIndex((p) => p.id === phase));
  const finished = phase === "done";

  return (
    <Shell
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 20h16" />
          <path d="M14.5 4.5a2.1 2.1 0 0 1 3 3L8 17l-4 1 1-4Z" />
        </svg>
      }
      title="Writing the proposal"
      width={640}
      meta={
        !error ? (
          <span className="text-[11.5px] font-semibold tabular-nums text-[#8E4187]">
            {words > 0 ? `${words.toLocaleString()} words` : `${Math.round(pct)}%`}
          </span>
        ) : null
      }
    >
      {error ? (
        <p role="alert" className="px-4 py-3.5 text-[13px] leading-relaxed text-[#B91C1C]">
          {error}
        </p>
      ) : (
        <div className="px-4 py-3.5">
          {/* A determinate bar, because the worker sends real milestones. */}
          <div
            className="h-1 w-full overflow-hidden rounded-full bg-[#18101C]/[0.07]"
            role="progressbar"
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Proposal progress"
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#A64E9D] to-[#C86B4F] transition-[width] duration-500 ease-out motion-reduce:transition-none"
              style={{ width: `${Math.min(100, Math.max(4, pct))}%` }}
            />
          </div>

          <ol className="mt-3 space-y-1.5">
            {PHASES.map((p, i) => {
              const state = finished || i < activeIdx ? "done" : i === activeIdx ? "now" : "todo";
              return (
                <li key={p.id} className="flex items-center gap-2.5">
                  <span className="flex h-3 w-3 flex-none items-center justify-center">
                    <Tick state={state} />
                  </span>
                  <span
                    className={`text-[12.5px] ${
                      state === "todo"
                        ? "text-[#9CA3AF]"
                        : state === "now"
                        ? "font-medium text-[#18101C]"
                        : "text-[#52525B]"
                    }`}
                  >
                    {p.label}
                  </span>
                </li>
              );
            })}
          </ol>

          <p className="mt-2.5 text-[12px] text-[#77707E]">{note}</p>

          {stalled && (
            <p className="mt-1.5 text-[12px] text-[#B45309]">
              Still going — no update for a while. If nothing moves in the next
              minute or two, ask me to try again.
            </p>
          )}

          {tail && (
            <div
              ref={tailRef}
              aria-hidden="true"
              className="mt-2.5 max-h-[168px] overflow-y-auto rounded-lg bg-[#18101C]/[0.03] px-3 py-2 font-doc text-[12.5px] leading-relaxed text-[#52525B]"
            >
              {tail}
            </div>
          )}
        </div>
      )}
    </Shell>
  );
};

export default ProposalOneGoCard;
